"use client";

import { useId } from "react";
import { isMovementActivityPostPayload } from "@/lib/movement/activity-post-payload";
import type { WalkingTemplateData } from "../walking-template";
import { MovementActivityDisplayPreview } from "./movement-activity-display-preview";
import { ActivityScenePreview } from "./reference-preview-primitives";

type WalkingDisplayPreviewProps = {
  data: WalkingTemplateData | null;
  participants: {
    id: string;
    displayName: string;
    avatarUrl: string | null;
    username: string | null;
  }[];
};

/*
 * A city park on a clear morning: low sun through the trees on the right, a
 * hazy skyline beyond the tree line, and a gravel path winding out of the
 * foreground into the distance. A lamp post and a bench (with a coffee) hold
 * the left third; a big oak frames the right.
 *
 * One panoramic SVG (640×220, `slice`): a 301px feed card shows the composed
 * centre, a full-width post reveals more park. The path's near end sits under
 * the bottom-centre participant pill. Motion is one compositor-only CSS loop
 * (the sun rays breathing), off under reduced motion.
 */
const WALKING_KEYFRAMES = `
@keyframes walk-rays { 0%, 100% { opacity: 0.7 } 50% { opacity: 1 } }
.walk-rays { animation: walk-rays 6s ease-in-out infinite }
@media (prefers-reduced-motion: reduce) { .walk-rays { animation: none } }
`;

type Point = { x: number; y: number };

const bezier = (p: Point[], t: number): Point => {
  const u = 1 - t;
  return {
    x:
      u * u * u * p[0].x +
      3 * u * u * t * p[1].x +
      3 * u * t * t * p[2].x +
      t * t * t * p[3].x,
    y:
      u * u * u * p[0].y +
      3 * u * u * t * p[1].y +
      3 * u * t * t * p[2].y +
      t * t * t * p[3].y,
  };
};

/** The path as a perspective ribbon whose half-width shrinks with distance. */
const PATH = (() => {
  const centre: Point[] = [
    { x: 330, y: 230 },
    { x: 420, y: 168 },
    { x: 220, y: 146 },
    { x: 300, y: 118 },
  ];
  const steps = 28;
  const left: Point[] = [];
  const right: Point[] = [];
  for (let i = 0; i <= steps; i++) {
    const t = i / steps;
    const p = bezier(centre, t);
    const w = 1.5 + 92 * (1 - t) ** 1.9;
    left.push({ x: p.x - w, y: p.y });
    right.push({ x: p.x + w, y: p.y });
  }
  const fmt = (p: Point) => `${p.x.toFixed(1)} ${p.y.toFixed(1)}`;
  const edge = (pts: Point[]) =>
    pts.map((p, i) => `${i === 0 ? "M" : "L"}${fmt(p)}`).join(" ");
  return {
    surface: `${edge(left)} ${right
      .slice()
      .reverse()
      .map((p) => `L${fmt(p)}`)
      .join(" ")} Z`,
    left: edge(left),
    right: edge(right),
  };
})();

/** A rounded tree canopy built from overlapping, sun-lit blobs. */
function Canopy({
  x,
  y,
  r,
  shade,
  base,
  light,
}: {
  x: number;
  y: number;
  r: number;
  shade: string;
  base: string;
  light: string;
}) {
  return (
    <g>
      <circle cx={x - r * 0.55} cy={y + r * 0.2} r={r * 0.7} fill={shade} />
      <circle cx={x + r * 0.5} cy={y + r * 0.25} r={r * 0.72} fill={base} />
      <circle cx={x} cy={y - r * 0.2} r={r * 0.8} fill={base} />
      <circle cx={x + r * 0.35} cy={y - r * 0.35} r={r * 0.42} fill={light} />
      <circle cx={x + r * 0.75} cy={y + r * 0.1} r={r * 0.3} fill={light} />
    </g>
  );
}

export function WalkingDisplayPreview({
  data,
  participants,
}: WalkingDisplayPreviewProps) {
  const uid = useId().replace(/[^\w-]/g, "");
  const id = (name: string) => `walk-${uid}-${name}`;
  const url = (name: string) => `url(#${id(name)})`;

  if (isMovementActivityPostPayload(data)) {
    return (
      <MovementActivityDisplayPreview
        activityId="walking"
        label="Walking"
        payload={data}
      />
    );
  }

  return (
    <ActivityScenePreview
      label="Walking"
      backgroundClassName="bg-[#3e7fb8]"
      participants={participants}
      scene={
        <>
          <style href="walking-scene-keyframes" precedence="default">
            {WALKING_KEYFRAMES}
          </style>
          <svg
            className="absolute inset-0 h-full w-full"
            viewBox="0 0 640 220"
            preserveAspectRatio="xMidYMid slice"
            aria-hidden="true"
            focusable="false"
          >
            <defs>
              <linearGradient id={id("sky")} x1="0" x2="0" y1="0" y2="1">
                <stop offset="0" stopColor="#3f86c8" />
                <stop offset="0.5" stopColor="#8cc6ec" />
                <stop offset="0.85" stopColor="#ffe7c2" />
              </linearGradient>
              <radialGradient id={id("sun")}>
                <stop offset="0" stopColor="#fffdf0" stopOpacity="1" />
                <stop offset="0.15" stopColor="#fff3c4" stopOpacity="0.9" />
                <stop offset="0.45" stopColor="#ffe7a0" stopOpacity="0.3" />
                <stop offset="1" stopColor="#ffe7a0" stopOpacity="0" />
              </radialGradient>
              <linearGradient id={id("ray")} x1="1" x2="0" y1="0" y2="1">
                <stop offset="0" stopColor="#fff6d6" stopOpacity="0.55" />
                <stop offset="1" stopColor="#fff6d6" stopOpacity="0" />
              </linearGradient>
              <linearGradient id={id("lawn")} x1="0" x2="0" y1="0" y2="1">
                <stop offset="0" stopColor="#9bcf5e" />
                <stop offset="0.4" stopColor="#5fa83f" />
                <stop offset="1" stopColor="#2f6e2a" />
              </linearGradient>
              <linearGradient id={id("path")} x1="0" x2="0" y1="0" y2="1">
                <stop offset="0" stopColor="#fbecc8" />
                <stop offset="0.5" stopColor="#e8cf9e" />
                <stop offset="1" stopColor="#c9a874" />
              </linearGradient>
              <pattern
                id={id("gravel")}
                width="6"
                height="5"
                patternUnits="userSpaceOnUse"
              >
                <rect
                  x="1"
                  y="1"
                  width="1.2"
                  height="1"
                  fill="#8a6a40"
                  opacity="0.35"
                />
                <rect
                  x="4"
                  y="3"
                  width="1"
                  height="1"
                  fill="#fff"
                  opacity="0.4"
                />
              </pattern>
              <linearGradient id={id("bark")} x1="0" x2="1" y1="0" y2="0">
                <stop offset="0" stopColor="#2b1d14" />
                <stop offset="0.6" stopColor="#5a3d26" />
                <stop offset="1" stopColor="#a07a4e" />
              </linearGradient>
              <linearGradient id={id("wood")} x1="0" x2="0" y1="0" y2="1">
                <stop offset="0" stopColor="#d79a5a" />
                <stop offset="1" stopColor="#8a5528" />
              </linearGradient>
            </defs>

            <rect width="640" height="140" fill={url("sky")} />

            {/* Morning sun, upper right, with rays raking across the park */}
            <circle cx="470" cy="34" r="90" fill={url("sun")} />
            <circle cx="470" cy="34" r="11" fill="#fffef6" />
            <g className="walk-rays">
              <polygon points="470,34 300,220 360,220" fill={url("ray")} />
              <polygon points="470,34 160,220 230,220" fill={url("ray")} />
              <polygon
                points="470,34 40,170 60,200"
                fill={url("ray")}
                opacity="0.6"
              />
            </g>

            {/* Soft clouds */}
            {[
              { x: 120, y: 34, s: 1 },
              { x: 280, y: 52, s: 0.7 },
              { x: 590, y: 44, s: 0.85 },
            ].map((cloud) => (
              <g key={cloud.x} fill="#fff" opacity="0.85">
                <ellipse
                  cx={cloud.x}
                  cy={cloud.y}
                  rx={26 * cloud.s}
                  ry={7 * cloud.s}
                />
                <circle
                  cx={cloud.x - 8 * cloud.s}
                  cy={cloud.y - 5 * cloud.s}
                  r={8 * cloud.s}
                />
                <circle
                  cx={cloud.x + 6 * cloud.s}
                  cy={cloud.y - 7 * cloud.s}
                  r={10 * cloud.s}
                />
              </g>
            ))}

            {/* Hazy skyline beyond the park */}
            <path
              d="M-10 118 L-10 100 L20 100 L20 88 L44 88 L44 104 L70 104 L70 80 L84 74 L98 80 L98 106 L128 106 L128 92 L150 92 L150 108 L380 108 L380 86 L396 86 L396 70 L410 70 L410 86 L426 86 L426 104 L452 104 L452 94 L478 94 L478 82 L500 82 L500 106 L540 106 L540 90 L566 90 L566 100 L600 100 L600 84 L620 84 L620 104 L650 104 L650 118 Z"
              fill="#9ab8d6"
              opacity="0.75"
            />

            {/* Tree line, far → near */}
            {[
              { x: 20, y: 104, r: 22 },
              { x: 70, y: 100, r: 26 },
              { x: 130, y: 106, r: 20 },
              { x: 186, y: 102, r: 24 },
              { x: 240, y: 108, r: 18 },
              { x: 360, y: 108, r: 18 },
              { x: 520, y: 104, r: 24 },
              { x: 580, y: 100, r: 28 },
              { x: 630, y: 106, r: 20 },
            ].map((tree) => (
              <Canopy
                key={tree.x}
                x={tree.x}
                y={tree.y}
                r={tree.r}
                shade="#3d7a4a"
                base="#55935a"
                light="#8cc274"
              />
            ))}

            {/* Lawn with mowing stripes */}
            <path
              d="M-10 118 Q320 108 650 118 L650 220 L-10 220 Z"
              fill={url("lawn")}
            />
            {[0, 1, 2, 3].map((i) => (
              <path
                key={i}
                d={`M-10 ${132 + i * 22} Q320 ${124 + i * 24} 650 ${132 + i * 22}`}
                fill="none"
                stroke="#fff"
                strokeOpacity="0.07"
                strokeWidth={6 + i * 4}
              />
            ))}
            {/* Sunlit patch on the lawn */}
            <ellipse
              cx="300"
              cy="166"
              rx="150"
              ry="30"
              fill="#fff4c0"
              opacity="0.18"
            />

            {/* Pond beyond the bend, with a pair of ducks */}
            <ellipse cx="392" cy="131" rx="40" ry="7" fill="#3e8a5a" />
            <ellipse cx="392" cy="130.4" rx="36" ry="5.4" fill="#8cc8ec" />
            <ellipse
              cx="384"
              cy="129.4"
              rx="20"
              ry="1.6"
              fill="#e8f6ff"
              opacity="0.8"
            />
            {[
              { x: 400, y: 131 },
              { x: 410, y: 132.4 },
            ].map((duck) => (
              <g key={duck.x}>
                <ellipse
                  cx={duck.x}
                  cy={duck.y}
                  rx="3"
                  ry="1.5"
                  fill="#f6f2ea"
                />
                <circle
                  cx={duck.x + 2.4}
                  cy={duck.y - 1.8}
                  r="1.2"
                  fill="#2f6e3a"
                />
                <path
                  d={`M${duck.x + 3.4} ${duck.y - 1.8} l1.4 0.4 l-1.4 0.3 Z`}
                  fill="#f2a23a"
                />
              </g>
            ))}

            {/* The gravel path */}
            <path d={PATH.surface} fill={url("path")} />
            <path d={PATH.surface} fill={url("gravel")} />
            <path
              d={PATH.left}
              fill="none"
              stroke="#8a6a3e"
              strokeOpacity="0.55"
              strokeWidth="1.6"
            />
            <path
              d={PATH.right}
              fill="none"
              stroke="#fff6dc"
              strokeOpacity="0.7"
              strokeWidth="1.2"
            />

            {/* Flower beds along the lawn */}
            {[
              { x: 172, y: 178, c: "#ff7aa8" },
              { x: 188, y: 186, c: "#ffd34a" },
              { x: 204, y: 180, c: "#ff7aa8" },
              { x: 462, y: 182, c: "#ffd34a" },
              { x: 448, y: 190, c: "#ff9a6a" },
              { x: 476, y: 192, c: "#ff7aa8" },
              { x: 410, y: 140, c: "#ffd34a" },
              { x: 424, y: 144, c: "#ff7aa8" },
            ].map((flower) => (
              <g key={`${flower.x}-${flower.y}`}>
                <circle
                  cx={flower.x}
                  cy={flower.y + 2}
                  r="4.4"
                  fill="#2f6e2a"
                />
                <circle cx={flower.x} cy={flower.y} r="2.4" fill={flower.c} />
                <circle
                  cx={flower.x - 0.6}
                  cy={flower.y - 0.6}
                  r="0.8"
                  fill="#fff"
                  opacity="0.7"
                />
              </g>
            ))}

            {/* Big oak framing the right */}
            <g>
              <ellipse
                cx="452"
                cy="166"
                rx="34"
                ry="5"
                fill="#1f4a1e"
                opacity="0.5"
              />
              <path
                d="M444 166 L447 96 Q450 84 456 96 L460 166 Z"
                fill={url("bark")}
              />
              <path
                d="M450 116 Q436 104 424 100"
                stroke="#3a2818"
                strokeWidth="4"
                fill="none"
                strokeLinecap="round"
              />
              <path
                d="M455 106 Q470 94 484 92"
                stroke="#3a2818"
                strokeWidth="4"
                fill="none"
                strokeLinecap="round"
              />
              <Canopy
                x={432}
                y={70}
                r={34}
                shade="#2a5e32"
                base="#3f7f3e"
                light="#7fbf5a"
              />
              <Canopy
                x={482}
                y={58}
                r={38}
                shade="#2a5e32"
                base="#3f7f3e"
                light="#8fcf62"
              />
              <Canopy
                x={456}
                y={88}
                r={26}
                shade="#24522c"
                base="#386f38"
                light="#6aa94c"
              />
            </g>

            {/* Lamp post, left third */}
            <g>
              <ellipse
                cx="190"
                cy="160"
                rx="10"
                ry="2.4"
                fill="#173d18"
                opacity="0.6"
              />
              <rect x="188.4" y="80" width="3.2" height="80" fill="#1f2a2e" />
              <rect
                x="186"
                y="154"
                width="8"
                height="6"
                rx="1"
                fill="#1f2a2e"
              />
              <path d="M182 80 L198 80 L195 70 L185 70 Z" fill="#26343a" />
              <rect
                x="185.4"
                y="71"
                width="9.2"
                height="8"
                fill="#fff4cc"
                opacity="0.9"
              />
              <path d="M184 70 L196 70 L190 64 Z" fill="#1f2a2e" />
              <rect
                x="190.6"
                y="82"
                width="0.8"
                height="70"
                fill="#b8d0d8"
                opacity="0.5"
              />
            </g>

            {/* Bench with a takeaway coffee, left third */}
            <g transform="translate(200 136)">
              <ellipse
                cx="28"
                cy="27"
                rx="32"
                ry="3.4"
                fill="#173d18"
                opacity="0.5"
              />
              {/* Iron legs */}
              <path
                d="M6 14 L4 27 M50 14 L52 27"
                stroke="#1f2a2e"
                strokeWidth="2.4"
              />
              <path
                d="M8 2 L6 14 M48 2 L50 14"
                stroke="#1f2a2e"
                strokeWidth="2"
              />
              {/* Back slats */}
              <rect
                x="2"
                y="0"
                width="54"
                height="3.2"
                rx="1"
                fill={url("wood")}
              />
              <rect
                x="2"
                y="5"
                width="54"
                height="3.2"
                rx="1"
                fill={url("wood")}
              />
              {/* Seat slats */}
              <rect
                x="0"
                y="12"
                width="58"
                height="3.4"
                rx="1"
                fill={url("wood")}
              />
              <rect x="1" y="15.6" width="56" height="2" fill="#6b3e1c" />
              <rect
                x="2"
                y="0"
                width="54"
                height="0.8"
                fill="#ffd9a0"
                opacity="0.7"
              />
              <rect
                x="0"
                y="12"
                width="58"
                height="0.8"
                fill="#ffd9a0"
                opacity="0.7"
              />
              {/* Coffee */}
              <path d="M40 4 L48 4 L47 12 L41 12 Z" fill="#f6efe4" />
              <rect x="40.6" y="6.6" width="6.8" height="2.6" fill="#7a4a2a" />
              <rect
                x="39.4"
                y="2.6"
                width="9.2"
                height="1.8"
                rx="0.8"
                fill="#3a2a22"
              />
            </g>

            {/* Pines framing the outer edges (seen on wider cards) */}
            {[
              { x: 36, h: 60 },
              { x: 604, h: 64 },
            ].map((tree) => (
              <g key={tree.x}>
                <polygon
                  points={`${tree.x},${190 - tree.h} ${tree.x - tree.h * 0.3},190 ${tree.x + tree.h * 0.3},190`}
                  fill="#1f5228"
                />
                <polygon
                  points={`${tree.x},${190 - tree.h} ${tree.x + tree.h * 0.3},190 ${tree.x + tree.h * 0.08},190`}
                  fill="#4f9a3e"
                  opacity="0.6"
                />
              </g>
            ))}
          </svg>
        </>
      }
      overlays={
        <div className="pointer-events-none absolute inset-0 z-10 bg-[radial-gradient(ellipse_115%_125%_at_50%_45%,transparent_55%,rgba(10,30,20,0.38)_100%)]" />
      }
    />
  );
}
